import { toast } from "react-hot-toast";
import { useSendSchemeReminderMutation } from "../../../features/tenant/scheme/schemeApi";

export const REMINDER_KINDS = {
  scheme_end: { title: "scheme-ending", dateKey: "scheme_end", dateLabel: "scheme end", audience: "all eligible users" },
  redeem_end: { title: "redemption-closing", dateKey: "redeem_end", dateLabel: "redeem end", audience: "users with points left to redeem" },
};

const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

/** True when the scheme is active and the date for this reminder is still in the future. */
export const canSendReminder = (scheme, kind) => {
  const end = scheme?.[REMINDER_KINDS[kind].dateKey];
  return String(scheme?.status) === "1" && !!end && new Date(end) > new Date();
};

/**
 * Manual "send reminder now" for a scheme (shared by the Schemes list and Scheme Details).
 * Shows a confirmation toast; warns when it is earlier than the configured "X days before"
 * start, or when no automatic reminder is configured. Returns { sendReminder(scheme, kind), sending }.
 */
export const useSchemeReminderSender = () => {
  const [sendSchemeReminder, { isLoading: sending }] = useSendSchemeReminderMutation();

  const sendReminder = (scheme, kind) => {
    if (sending) return;
    const meta = REMINDER_KINDS[kind];
    const cfg = scheme?.settings?.notifications?.[kind];
    const endAt = scheme?.[meta.dateKey] ? new Date(scheme[meta.dateKey]) : null;

    let warning = "";
    if (!cfg) {
      warning = "No automatic reminder is configured for this scheme. This sends a one-time push now.";
    } else if (endAt && !Number.isNaN(endAt.getTime())) {
      // The stored end is the boundary after the picked end date; count from the picked (last) day.
      const start = new Date(endAt.getTime() - 1);
      start.setDate(start.getDate() - Number(cfg.days_before));
      start.setHours(0, 0, 0, 0);
      if (new Date() < start) {
        warning = `The automatic reminder is set to start on ${fmt(start)} (${cfg.days_before} day(s) before ${meta.dateLabel}). Sending now is earlier than configured.`;
      }
    }

    const auth = {
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id,
    };

    toast(
      (t) => (
        <span onClick={(e) => e.stopPropagation()}>
          <p className="font-medium text-sm text-gray-800">
            Send {meta.title} reminder for <span className="font-semibold">"{scheme.name}"</span> now?
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Goes to {meta.audience} with a registered device.</p>
          {warning && <p className="text-xs text-amber-700 mt-1.5">⚠ {warning}</p>}
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  const res = await sendSchemeReminder({ ...auth, id: scheme.id, kind }).unwrap();
                  toast.success(res?.message || "Reminder sent");
                } catch (err) {
                  toast.error(err?.data?.message || "Failed to send reminder");
                }
              }}
            >
              {warning ? "Send anyway" : "Send now"}
            </button>
            <button
              className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "scheme-reminder", duration: 12000 },
    );
  };

  return { sendReminder, sending };
};
